import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  InputAdornment,
  CircularProgress,
  Tooltip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Stack,
  Divider,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import DescriptionIcon from "@mui/icons-material/Description";
import toast from "react-hot-toast";
import configServ from "../../services/config";

const DocumentType = () => {
  const [documentTypes, setDocumentTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [filterActive, setFilterActive] = useState("all");

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Dialog State (Create / Edit)
  const [openModal, setOpenModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    active: true,
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitLoading, setSubmitLoading] = useState(false);

  // Delete Confirmation State
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Fetch document types from backend
  const fetchDocumentTypes = useCallback(async () => {
    setLoading(true);
    try {
      const payload = {
        page: page + 1,
        per_page: rowsPerPage,
      };

      if (search.trim()) {
        payload.search = search.trim();
      }

      if (filterActive !== "all") {
        payload.active = filterActive === "active";
      }

      const res = await configServ.getDocumentTypes(payload);
      if (res && res.status) {
        setDocumentTypes(res.data || []);
        setTotalCount(res.total ?? (res.data ? res.data.length : 0));
      } else {
        setDocumentTypes(res.data || []);
      }
    } catch (error) {
      console.error("Error fetching document types:", error);
      toast.error(error?.response?.data?.message || "Failed to load document types");
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, search, filterActive]);

  useEffect(() => {
    fetchDocumentTypes();
  }, [fetchDocumentTypes]);

  // Modal Handlers
  const handleOpenAdd = () => {
    setIsEditing(false);
    setCurrentId(null);
    setFormData({ name: "", active: true });
    setFormErrors({});
    setOpenModal(true);
  };

  const handleOpenEdit = (item) => {
    setIsEditing(true);
    setCurrentId(item.id);
    setFormData({
      name: item.name || "",
      active: Boolean(item.active),
    });
    setFormErrors({});
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setFormData({ name: "", active: true });
    setFormErrors({});
    setIsEditing(false);
    setCurrentId(null);
  };

  const handleFormChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) {
      errors.name = "Document Type Name is required";
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitLoading(true);
    try {
      if (isEditing) {
        const res = await configServ.updateDocumentType({
          id: currentId,
          name: formData.name.trim(),
          active: formData.active,
        });
        if (res && res.status) {
          toast.success("Document Type updated successfully!");
          handleCloseModal();
          fetchDocumentTypes();
        } else {
          toast.error(res?.message || "Failed to update document type");
        }
      } else {
        const res = await configServ.createDocumentType({
          name: formData.name.trim(),
          active: formData.active,
        });
        if (res && res.status) {
          toast.success("Document Type created successfully!");
          handleCloseModal();
          fetchDocumentTypes();
        } else {
          toast.error(res?.message || "Failed to create document type");
        }
      }
    } catch (error) {
      console.error("Error saving document type:", error);
      toast.error(error?.response?.data?.message || "Something went wrong");
    } finally {
      setSubmitLoading(false);
    }
  };

  // Delete Handlers
  const handleOpenDelete = (item) => {
    setDeleteTarget(item);
    setDeleteDialogOpen(true);
  };

  const handleCloseDelete = () => {
    setDeleteDialogOpen(false);
    setDeleteTarget(null);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;

    setDeleteLoading(true);
    try {
      const res = await configServ.deleteDocumentType({ id: deleteTarget.id });
      if (res && res.status) {
        toast.success("Document Type deleted successfully!");
        handleCloseDelete();
        fetchDocumentTypes();
      } else {
        toast.error(res?.message || "Failed to delete document type");
      }
    } catch (error) {
      console.error("Error deleting document type:", error);
      toast.error(error?.response?.data?.message || "Failed to delete document type");
    } finally {
      setDeleteLoading(false);
    }
  };


  return (
    <Box sx={{ width:"100%"}}>
      {/* Header Section */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          background: "linear-gradient(to right, #ffffff, #f8fafc)",
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={2}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <Box
              sx={{
                p: 1.2,
                borderRadius: 2,
                bgcolor: "primary.50",
                color: "primary.main",
                display: "flex",
              }}
            >
              <DescriptionIcon sx={{ fontSize: 28 }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={700} color="text.primary">
                Document Types
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage and configure document categories for your platform
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleOpenAdd}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              px: 3,
              py: 1,
              borderRadius: 2,
              boxShadow: "0px 4px 12px rgba(25, 118, 210, 0.25)",
            }}
          >
            Add Document Type
          </Button>
        </Stack>

        <Divider sx={{ my: 2.5 }} />

        {/* Professional Filter and Search Bar */}
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems="center"
        >
          <TextField
            size="small"
            placeholder="Search document type name..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            sx={{
              flex: 1,
              minWidth: { xs: "100%", sm: 280 },
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                bgcolor: "background.paper",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  borderColor: "primary.main",
                },
                "&.Mui-focused": {
                  boxShadow: "0 0 0 3px rgba(25, 118, 210, 0.12)",
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "action.active", fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
          />

          <FormControl size="small" sx={{ minWidth: 160, width: { xs: "100%", sm: "auto" } }}>
            <InputLabel id="filter-status-label">Status</InputLabel>
            <Select
              labelId="filter-status-label"
              value={filterActive}
              label="Status"
              onChange={(e) => {
                setFilterActive(e.target.value);
                setPage(0);
              }}
              sx={{ borderRadius: 2 }}
            >
              <MenuItem value="all">All Status</MenuItem>
              <MenuItem value="active">Active Only</MenuItem>
              <MenuItem value="inactive">Inactive Only</MenuItem>
            </Select>
          </FormControl>

          <Tooltip title="Reset Filters & Refresh">
            <IconButton
              onClick={() => {
                setSearch("");
                setFilterActive("all");
                setPage(0);
                fetchDocumentTypes();
              }}
              sx={{
                bgcolor: "action.hover",
                borderRadius: 2,
                p: 1,
                "&:hover": { bgcolor: "action.selected" },
              }}
            >
              <RefreshIcon color="action" fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Paper>

      {/* Table Section */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          overflow: "hidden",
          border: "1px solid",
          borderColor: "divider",
          boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)",
        }}
      >
        <TableContainer>
          <Table sx={{ minWidth: 600 }}>
            {/* Black Modern Header */}
            <TableHead
              sx={{
                bgcolor: "#1E293B",
                "& .MuiTableCell-head": {
                  color: "#FFFFFF",
                  fontWeight: 700,
                  fontSize: "0.875rem",
                  letterSpacing: "0.5px",
                  py: 1.8,
                  borderBottom: "none",
                },
              }}
            >
              <TableRow>
                <TableCell sx={{ width: 80, pl: 3 }}>#</TableCell>
                <TableCell>DOCUMENT TYPE NAME</TableCell>
                <TableCell sx={{ width: 160 }}>STATUS</TableCell>
                <TableCell sx={{ width: 140, pr: 3, textAlign: "center" }}>
                  ACTIONS
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={36} />
                    <Typography variant="body2" sx={{ mt: 1.5 }} color="text.secondary">
                      Loading document types...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : documentTypes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                    <DescriptionIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
                    <Typography variant="subtitle1" fontWeight={600} color="text.secondary">
                      No document types found
                    </Typography>
                    <Typography variant="body2" color="text.disabled">
                      Click "Add Document Type" to create one.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                documentTypes.map((item, index) => (
                  <TableRow
                    key={item.id}
                    hover
                    sx={{
                      transition: "background-color 0.15s ease",
                      "&:hover": { bgcolor: "#F8FAFC" },
                      "&:last-child td, &:last-child th": { border: 0 },
                    }}
                  >
                    <TableCell sx={{ pl: 3, color: "text.secondary", fontWeight: 500 }}>
                      {page * rowsPerPage + index + 1}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600} color="text.primary">
                        {item.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={item.active ? "Active" : "Inactive"}
                        color={item.active ? "success" : "default"}
                        size="small"
                        sx={{
                          fontWeight: 600,
                          borderRadius: 1.5,
                          px: 0.5,
                        }}
                        variant={item.active ? "filled" : "outlined"}
                      />
                    </TableCell>
                    <TableCell align="center" sx={{ pr: 3 }}>
                      <Tooltip title="Edit">
                        <IconButton
                          color="primary"
                          size="small"
                          onClick={() => handleOpenEdit(item)}
                          sx={{
                            bgcolor: "primary.50",
                            mr: 1,
                            "&:hover": { bgcolor: "primary.100" },
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => handleOpenDelete(item)}
                          sx={{
                            bgcolor: "error.50",
                            "&:hover": { bgcolor: "error.100" },
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
          sx={{ borderTop: "1px solid", borderColor: "divider" }}
        />
      </Paper>

      {/* Add / Edit Dialog */}
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <form onSubmit={handleSubmit}>
          <DialogTitle fontWeight={700}>
            {isEditing ? "Edit Document Type" : "Add New Document Type"}
          </DialogTitle>
          <DialogContent dividers sx={{ py: 3 }}>
            <TextField
              autoFocus
              margin="dense"
              name="name"
              label="Document Type Name"
              type="text"
              fullWidth
              variant="outlined"
              value={formData.name}
              onChange={handleFormChange}
              error={Boolean(formErrors.name)}
              helperText={formErrors.name}
              placeholder="e.g. Identity Proof, Address Proof, Passport"
              sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.active}
                  onChange={handleFormChange}
                  name="active"
                  color="primary"
                />
              }
              label={
                <Typography variant="body2" fontWeight={600}>
                  {formData.active ? "Active" : "Inactive"}
                </Typography>
              }
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={handleCloseModal} color="inherit" disabled={submitLoading} sx={{ borderRadius: 2 }}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={submitLoading}
              startIcon={submitLoading && <CircularProgress size={16} />}
              sx={{ borderRadius: 2, px: 3 }}
            >
              {isEditing ? "Save Changes" : "Create"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDelete}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle fontWeight={700}>Confirm Delete</DialogTitle>
        <DialogContent dividers sx={{ py: 2.5 }}>
          <Typography variant="body1">
            Are you sure you want to delete <strong>"{deleteTarget?.name}"</strong>?
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseDelete} color="inherit" disabled={deleteLoading} sx={{ borderRadius: 2 }}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            disabled={deleteLoading}
            startIcon={deleteLoading && <CircularProgress size={16} />}
            sx={{ borderRadius: 2, px: 3 }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DocumentType;

